import { prefix } from "./helper";
import type { UseLocalStorageKeysKeys } from "./localStorageKeys";
import { localStorageKeys } from "./localStorageKeys";

// Helper to get the actual storage key value for a key name
export const getLocalStorageKeyValue = (
  keyName: UseLocalStorageKeysKeys
): string => {
  return localStorageKeys[keyName]?.value || "";
};

// Create the 'localStorageKeyMap' object for dot notation access (key.keyname)
export const localStorageKeyMap: Record<
  UseLocalStorageKeysKeys,
  UseLocalStorageKeysKeys
> = Object.keys(localStorageKeys).reduce(
  (acc, keyName) => {
    acc[keyName as UseLocalStorageKeysKeys] =
      keyName as UseLocalStorageKeysKeys;
    return acc;
  },
  {} as Record<UseLocalStorageKeysKeys, UseLocalStorageKeysKeys>
);

// Create the mapping from key names to key values
export const useLocalStorageKeys: Record<
  UseLocalStorageKeysKeys,
  UseLocalStorageKeysKeys
> = Object.keys(localStorageKeys).reduce(
  (acc, keyName) => {
    acc[keyName as UseLocalStorageKeysKeys] =
      keyName as UseLocalStorageKeysKeys;
    return acc;
  },
  {} as Record<UseLocalStorageKeysKeys, UseLocalStorageKeysKeys>
);

// Helper to get max length for a key (used in reducers)
export const getMaxLength = (keyName: UseLocalStorageKeysKeys): number => {
  return localStorageKeys[keyName]?.maxLength || 20;
};

// Helper to get the description for a key
export const getLocalStorageItemDescription = (
  keyName: UseLocalStorageKeysKeys
): string => {
  return localStorageKeys[keyName]?.description || "No description available";
};

// Helper to get the friendly key name (without prefix)
export const getLocalStorageItemFriendlyKey = (
  keyName: UseLocalStorageKeysKeys
): string => {
  const value = localStorageKeys[keyName]?.value;
  return value ? value.split(prefix)[1] : "No friendly key available";
};

// Helper to get the friendly key name (without prefix)
export const getLocalStorageItemActualKey = (
  keyName: UseLocalStorageKeysKeys
): string => keyName;

// Helper to get the value for a key
export const getLocalStorageItemValue = (
  keyName: UseLocalStorageKeysKeys
): string =>
  getItemFromLocalStorageStr(localStorageKeys[keyName].value) || "null";

// Helper to get all local storage items
export const getLocalStorageItems = (): LocalStorageItem[] =>
  Object.keys(localStorageKeys).map(keyName => ({
    actualKey: keyName,
    friendlyKey: getLocalStorageItemFriendlyKey(
      keyName as UseLocalStorageKeysKeys
    ),
    value: getLocalStorageItemValue(keyName as UseLocalStorageKeysKeys),
    description: getLocalStorageItemDescription(
      keyName as UseLocalStorageKeysKeys
    )
  }));

// Helper to get a specific local storage item
export const getLocalStorageItem = (
  keyName: UseLocalStorageKeysKeys
): LocalStorageItem => ({
  actualKey: keyName,
  friendlyKey: getLocalStorageItemFriendlyKey(keyName),
  value: getLocalStorageItemValue(keyName),
  description: getLocalStorageItemDescription(keyName)
});

export interface LocalStorageItem {
  actualKey: string;
  friendlyKey: string;
  value: string;
  description: string;
}

export type { UseLocalStorageKeysKeys };
/* eslint-disable no-restricted-syntax */
export function setLocalStorageItem(key: string, data: Record<string, string>) {
  localStorage.setItem(key, data[key]);
}

export function getItemFromLocalStorage(item: LocalStorageItem): string | null {
  return localStorage.getItem(item.actualKey);
}

export function getItemFromLocalStorageStr(selectedKey: string) {
  return localStorage.getItem(selectedKey);
}
//* eslint-enable no-restricted-syntax */
